import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { List } from 'immutable';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';

import debounce from 'src/utils/debounce';
import uniqueId from 'src/utils/uniqueId';

import { Spinner } from './Spinner';

const ContextTriggerCellRenderer = memo(props => {
  const { rowIndex, data, colDef, value } = props;

  const collect = useCallback(
    () => ({
      rowIndex,
      rowData: data,
    }),
    [rowIndex, data],
  );

  return (
    <ContextMenuTrigger collect={collect} id={colDef.contextMenuId}>
      {value.value}
    </ContextMenuTrigger>
  );
});

const gridOverlayWrapperStyle = {
  position: 'relative',
  height: '100%',
};

const loadingOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
};

const setRowsStyles = params => {
  if (params.node.rowPinned) {
    return {
      'font-weight': 'bold',
      'border-top': '2px solid #000',
      'background-color': '#f6f6f6',
    };
  }
};

const getGridProps = props => {
  const keys = [
    'getRowClass',
    'onCellClicked',
    'onRowClicked',
    'onRowDoubleClicked',
    'onCellDoubleClicked',
    'onCellEditingStopped',
    'onColumnResized',
    'onCellValueChanged',
    'onFirstDataRendered',
    'onViewportChanged',
    'rowClassRules',
    'suppressCellSelection',
    'suppressNoRowsOverlay',
    'onRowEditingStopped',
    'onRowValueChanged',
    'onSelectionChanged',
    'editType',
    'headerHeight',
    'rowSelection',
    'getRowHeight',
    'rowHeight',
  ];

  return keys.reduce((prev, key) => {
    if (props[key]) {
      prev[key] = props[key];
    }

    return prev;
  }, {});
};

const emptyList = List();
const emptyStyle = {};

const AgGrid = memo(props => {
  const {
    rowData = emptyList,
    columnDefs = emptyList,
    page = 1,
    totalPages = 0,
    style = emptyStyle,
    isFetching = false,
    resizable = true,
    sortable = false,
    suppressMovableColumns = true,
    onContextMenuClick,
    contextActionList,
    onGetNextPageData,
    onGridReady,
    onSortModelChanged,
    sort,
    serverSideSorting,
    getRowNodeId: getRowNodeIdProp,
    ...rest
  } = props;

  const apiRef = useRef(null);
  const tableRef = useRef(null);
  const contextMenuId = useMemo(() => uniqueId(), []);

  const showLoading = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.showLoadingOverlay();
    }
  }, []);

  const hideLoading = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.hideOverlay();
      if (!rowData || rowData.size === 0) {
        apiRef.current.showNoRowsOverlay();
      }
    }
  }, [rowData]);

  useEffect(() => {
    if (isFetching) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [isFetching, showLoading, hideLoading]);

  const handleResize = useMemo(
    () =>
      debounce(() => {
        if (apiRef.current) {
          apiRef.current.sizeColumnsToFit();
        }
      }, 150),
    [],
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(tableRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  const handleBodyScroll = useCallback(
    event => {
      const agTable = tableRef.current.querySelector('.ag-full-width-container');
      const agBodyViewport = tableRef.current.querySelector('.ag-body-viewport');

      const scrollTop = Math.abs(parseInt(event.top, 10));
      const contentHeight = parseInt(agTable.style.height, 10);
      const viewportHeight = agBodyViewport.clientHeight;

      const isScrolledToBottom = scrollTop + viewportHeight >= contentHeight;

      if (!isScrolledToBottom) return;
      if (totalPages <= page) return;
      if (isFetching) return;

      if (onGetNextPageData) {
        onGetNextPageData(page + 1);
      }
    },
    [isFetching, onGetNextPageData, page, totalPages],
  );

  const handleGridReady = useCallback(
    params => {
      apiRef.current = params.api;

      if (isFetching) {
        showLoading();
      }

      if (onGridReady) {
        onGridReady(params);
      }
    },
    [isFetching, onGridReady, showLoading],
  );

  const handleSortChanged = useCallback(
    ({ columnApi }) => {
      if (onSortModelChanged) {
        const value = columnApi.getColumnState().filter(column => column.sort != null);

        onSortModelChanged(value);
      }
    },
    [onSortModelChanged],
  );

  const handleCellContextMenu = useCallback(
    cell => {
      if (onContextMenuClick) {
        cell.event.preventDefault();
      }
    },
    [onContextMenuClick],
  );

  const getRowNodeId = useCallback(
    data => {
      if (getRowNodeIdProp) {
        return getRowNodeIdProp(data);
      }

      return data.id || data.order || uniqueId();
    },
    [getRowNodeIdProp],
  );

  const { defs, contextMenu } = useMemo(() => {
    let nextColumnDefs = columnDefs.toJS();

    nextColumnDefs.forEach(colDef => {
      if (!colDef.comparator && serverSideSorting) {
        colDef.comparator = () => {};
      }

      if (colDef.headerName !== '# ') {
        colDef.sortable = colDef.sortable === false ? false : sortable;
      }

      colDef.resizable = resizable;
      colDef.sort = sort || null;
    });

    let nextContextMenu = null;

    if (onContextMenuClick) {
      nextContextMenu = (
        <ContextMenu id={contextMenuId}>
          {contextActionList.map((action, index) => (
            <MenuItem key={index} data={{ key: action.get('key') }} onClick={onContextMenuClick}>
              {action.get('name')}
            </MenuItem>
          ))}
        </ContextMenu>
      );

      nextColumnDefs = nextColumnDefs.map(columnDef => {
        columnDef.cellRendererFramework = columnDef.cellRendererFramework || ContextTriggerCellRenderer;
        columnDef.contextMenuId = contextMenuId;

        return columnDef;
      });
    }

    return { defs: nextColumnDefs, contextMenu: nextContextMenu };
  }, [columnDefs, contextActionList, contextMenuId, onContextMenuClick, resizable, serverSideSorting, sort, sortable]);

  const data = rowData.toJS();
  const gridProps = getGridProps(rest);

  return (
    <div ref={tableRef} className="ag-theme-fresh" style={style}>
      <div style={gridOverlayWrapperStyle}>
        <AgGridReact
          {...gridProps}
          columnDefs={defs}
          getRowNodeId={getRowNodeId}
          getRowStyle={params => setRowsStyles(params)}
          onBodyScroll={handleBodyScroll}
          onCellContextMenu={handleCellContextMenu}
          onGridReady={handleGridReady}
          onSortChanged={handleSortChanged}
          overlayLoadingTemplate="<span></span>"
          rowData={data}
          sortable={sortable}
          suppressMovableColumns={suppressMovableColumns}
          suppressNoRowsOverlay={isFetching}
          enableCellTextSelection
          ensureDomOrder
          immutableData
          reactNext
          suppressModelUpdateAfterUpdateTransaction
        />
        {isFetching && (
          <div className="ag-grid-loading-spinner" style={loadingOverlayStyle}>
            <Spinner />
          </div>
        )}
      </div>
      {contextMenu}
    </div>
  );
});

AgGrid.propTypes = {
  columnDefs: PropTypes.instanceOf(List).isRequired,
  getRowClass: PropTypes.func,
  isFetching: PropTypes.bool,
  onContextMenuClick: PropTypes.func,
  onGetNextPageData: PropTypes.func,
  onGridReady: PropTypes.func,
  page: PropTypes.number,
  resizable: PropTypes.bool,
  rowClassRules: PropTypes.instanceOf(Object),
  rowData: PropTypes.instanceOf(List).isRequired,
  sortable: PropTypes.bool,
  style: PropTypes.object,
  totalPages: PropTypes.number,
};

export default AgGrid;
